import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";

import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import { visuallyHidden } from "@mui/utils";
import { Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { add, addAll, remove } from "../../../store/receivableSlice";
import {
  documentNumberFormatter,
  formatMoney,
  dateFormatter,
} from "../../../services/UtilsService";
import DeleteConfirmation from "./DeleteConfirmation";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import SimpleDialog from "../../../components/Dialog/SimpleDialog";

function createData(
  uuid,
  nf_number,
  serial,
  issuedAt,
  importedAt,
  socialName,
  socialNumber,
  total,
  installmentsQuantity,
  paymentMethod,
  status,
  cashAdvanceUuid,
  conditionDescription
) {
  return {
    uuid,
    nf_number,
    serial,
    issuedAt,
    importedAt,
    socialName,
    socialNumber,
    total,
    installmentsQuantity,
    paymentMethod,
    status,
    cashAdvanceUuid,
    conditionDescription,
  };
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "nf-number",
    numeric: false,
    disablePadding: true,
    label: "Nº da NF-e • Série",
  },
  {
    id: "issuedAt",
    numeric: false,
    disablePadding: false,
    label: "Emissão",
  },
  {
    id: "socialNumber",
    numeric: false,
    disablePadding: false,
    label: "Destinatário",
  },
  {
    id: "installments",
    numeric: false,
    disablePadding: false,
    label: "Valor • Parcelas",
  },
  {
    id: "importedAt",
    numeric: true,
    disablePadding: false,
    label: "Criado em",
  },
  {
    id: "condicao",
    numeric: false,
    disablePadding: false,
    label: "Situação",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            sx={{ borderRight: "1px solid #E9EBF0", width: 100 }}
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{ color: "#575757", fontSize: 14, fontWeight: 400 }}
            >
              {headCell.label}
              {orderBy === headCell.id && (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const buttonStyle = {
  AVAILABLE: {
    textTransform: "none",
    borderRadius: 50,
    borderColor: "#269800",
    color: "#269800",
    "&:hover": {
      background: "#269800",
      color: "white",
      borderColor: "white",
    },
  },
  ANTICIPATED: {
    textTransform: "none",
    borderRadius: 50,
    borderColor: "#FF8500",
    color: "#FF8500",
    "&:hover": {
      background: "#FF8500",
      color: "white",
      borderColor: "white",
    },
  },
  REJECTED: {
    textTransform: "none",
    borderRadius: 50,
    borderColor: "#E71D36",
    color: "#E71D36",
    "&:hover": {
      background: "#E71D36",
      color: "white",
      borderColor: "white",
    },
  },
};

const buttonText = {
  AVAILABLE: "Simular agora",
  ANTICIPATED: "Ver detalhes",
  REJECTED: "Ver motivo",
};
const situacaoParse = {
  AVAILABLE: "Disponível",
  ANTICIPATED: "Antecipada",
  REJECTED: "Rejeitada",
};

const StyledLink = styled(Link)`
  justify-content: center;
  text-decoration: none;
  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

const ConditionButton = ({
  status,
  row,
  openModal,
  setModalContent,
  modalContent,
  openModalFunction,
}) => {
  const dispatch = useDispatch();

  const profileSlice = useSelector((state) => state.profileSlice);

  const defineNavigate = (status, row) => {
    if (status === "ANTICIPATED") {
      return `/dashboard/anticipation/detail/${row.cashAdvanceUuid}`;
    }
    if (status === "AVAILABLE") {
      if (profileSlice?.profile?.subscription?.cashAdvanceEnabled) {
        return "credit";
      }
      return "";
    }
    if (status === "REJECTED") {
      return "";
    }
    return "undefined";
  };

  return (
    <Box
      display={"flex"}
      width={"150px"}
      margin={"0px 12px"}
      justifyContent={"center"}
    >
      <Button
        fullWidth
        component={StyledLink}
        sx={buttonStyle[status]}
        variant="outlined"
        to={defineNavigate(status, row)}
        onClick={() => {
          if (status === "REJECTED") {
            setModalContent(modalContent);
            openModal(true);
            return;
          }

          if (!profileSlice?.profile?.subscription?.cashAdvanceEnabled) {
            dispatch(add(row.uuid));
            openModalFunction();
            return;
          }

          dispatch(add(row.uuid));
        }}
      >
        {buttonText[status]}
      </Button>
    </Box>
  );
};

const InvoiceTable = ({ receivables, searchText, openModalFunction }) => {
  const [rows, setRows] = React.useState([]);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  // const [dense, setDense] = React.useState(false);
  const [dense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  React.useEffect(() => {
    const rows = receivables.map((receivable) =>
      createData(
        receivable.uuid,
        receivable.notaFiscal?.number,
        receivable.notaFiscal?.serie,
        receivable.notaFiscal?.issuedAt,
        receivable.createdAt,
        receivable.notaFiscal?.tomador?.name,
        receivable.notaFiscal?.tomador?.documentNumber,
        receivable.notaFiscal?.totalAmount,
        receivable.notaFiscal?.bill?.numberOfInstallments,
        receivable.notaFiscal?.bill?.paymentType,
        receivable.condition,
        receivable.cashAdvanceUuid,
        receivable.conditionDescription
      )
    );

    const filteredReceivables = rows.filter(
      (row) =>
        row.nf_number.includes(searchText) ||
        row.socialNumber.includes(searchText)
    );

    filteredReceivables.length !== 0
      ? setRows(filteredReceivables)
      : setRows(rows);
  }, [receivables, searchText]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows
        .filter((n) => n.status === "AVAILABLE")
        .map((n) => n.uuid);
      setSelected(newSelected);
      dispatch(addAll(newSelected));
      return;
    }
    setSelected([]);
    dispatch(remove([]));
  };

  const dispatch = useDispatch();

  const handleClick = (event, row) => {
    if (row.status !== "AVAILABLE") {
      return;
    }

    const uuid = row.uuid;

    const selectedIndex = selected.indexOf(uuid);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, uuid);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    dispatch(addAll(newSelected));
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const handleChangeDense = (event) => {
  //   setDense(event.target.checked);
  // };

  const isSelected = (uuid) => selected.indexOf(uuid) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),

    [order, orderBy, page, rowsPerPage, rows]
  );

  const [openDeleteInvoiceConfirmation, setOpenDeleteInvoiceConfirmation] =
    React.useState(false);

  const [openSimpleDialog, setOpenSimpleDialog] = React.useState(false);

  const [simpleDialogContent, setSimpleDialogContent] = React.useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  //const [itemSelectedDelete, setItemSelectedDelete] = React.useState(null);

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size={dense ? "small" : "medium"}
        >
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.filter((row) => row.status === "AVAILABLE").length}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              const isItemSelected = isSelected(row.uuid);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  hover
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.uuid}
                  selected={isItemSelected}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(event) => handleClick(event, row)}
                  >
                    <Checkbox
                      color="primary"
                      disabled={row.status !== "AVAILABLE"}
                      checked={isItemSelected}
                      inputProps={{
                        "aria-labelledby": labelId,
                      }}
                      onClick={(event) => handleClick(event, row)}
                    />
                  </TableCell>
                  <TableCell
                    onClick={(event) => handleClick(event, row)}
                    component="th"
                    id={labelId}
                    scope="left"
                    padding="none"
                  >
                    <Typography sx={{ fontWeight: 400 }} color="inherit" noWrap>
                      {row.nf_number}-{row.serial}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="left"
                    onClick={(event) => handleClick(event, row)}
                  >
                    <Typography sx={{ fontWeight: 400 }} color="inherit" noWrap>
                      {dateFormatter(row.issuedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="left"
                    onClick={(event) => handleClick(event, row.uuid)}
                  >
                    <Typography sx={{ fontWeight: 400 }} color="inherit" noWrap>
                      {row.socialName}
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 400, color: "#8087A6" }}
                      color="inherit"
                      noWrap
                    >
                      {documentNumberFormatter(row.socialNumber)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="left"
                    onClick={(event) => handleClick(event, row)}
                  >
                    <Typography sx={{ fontWeight: 400 }} color="inherit" noWrap>
                      {formatMoney(row.total)}
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 400, color: "#8087A6" }}
                      color="inherit"
                      noWrap
                    >
                      {row.installmentsQuantity === 1 ||
                      !row.installmentsQuantity
                        ? "à vista"
                        : row.installmentsQuantity +
                          "x de " +
                          formatMoney(row.total / row.installmentsQuantity)}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Box
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography sx={{ fontWeight: 400 }} color="inherit">
                        {dateFormatter(row.importedAt)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box style={{ display: "flex" }}>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          lineHeight: "32px",
                          width: "100px",
                        }}
                      >
                        {situacaoParse[row.status]}
                      </Typography>
                      <ConditionButton
                        setModalContent={setSimpleDialogContent}
                        modalContent={row.conditionDescription}
                        openModal={setOpenSimpleDialog}
                        openModalFunction={openModalFunction}
                        status={row.status}
                        row={row}
                      />
                    </Box>
                    {/* <IconButton
                        padding={"0px"}
                        onClick={() => {
                          setItemSelectedDelete(row);
                          setOpenDeleteInvoiceConfirmation(true);
                        }}
                      >
                        <DeleteOutline sx={{ color: "#820AD1" }} />
                      </IconButton> */}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        labelRowsPerPage="Itens por página"
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <DeleteConfirmation
        open={openDeleteInvoiceConfirmation}
        setOpen={setOpenDeleteInvoiceConfirmation}
      />
      <SimpleDialog
        open={openSimpleDialog}
        setOpen={setOpenSimpleDialog}
        title={"Motivo da rejeição"}
        content={simpleDialogContent}
      />
    </Box>
  );
};

export default InvoiceTable;
